import api from 'api';
import {
  fipeClient,
  parallelumClient,
  getParallelumVehicleTypePath,
  toFipeVehicleType,
} from './parallelumClient';
import {
  mapBrandsFromFipe,
  mapBrandsFromParallelum,
  mapModelsFromFipe,
  mapModelsFromParallelum,
  mapYearsFromFipe,
  mapYearsFromParallelum,
} from './mappers';

// ---------------------------------------------------------------------------
// Cache da tabela de referência (muda mensalmente)
// ---------------------------------------------------------------------------

let _cachedReferenceCode = null;
let _cachedReferenceExpiry = 0;

/**
 * Obtém o código da tabela de referência mais recente da FIPE.
 * Faz cache por 24h pois a tabela muda mensalmente.
 *
 * Endpoint: POST /ConsultarTabelaDeReferencia
 * Retorna: [{ Codigo: 315, Mes: "abril/2026" }, ...]
 */
async function getReferenceTableCode() {
  const now = Date.now();
  if (_cachedReferenceCode && now < _cachedReferenceExpiry) {
    return _cachedReferenceCode;
  }

  const resp = await fipeClient.post('/ConsultarTabelaDeReferencia');
  const tables = resp?.data;

  if (!Array.isArray(tables) || tables.length === 0) {
    throw new Error('[fipeService] Não foi possível obter a tabela de referência da FIPE.');
  }

  // A primeira entrada é sempre a mais recente
  _cachedReferenceCode = tables[0].Codigo;
  _cachedReferenceExpiry = now + 24 * 60 * 60 * 1000; // 24h

  return _cachedReferenceCode;
}

// ---------------------------------------------------------------------------
// Listar Marcas
// ---------------------------------------------------------------------------

/**
 * Busca a lista de marcas de veículos.
 * Primário: API oficial FIPE → Fallback: Parallelum
 *
 * @param {{ type: number }} params — tipo do veículo (código interno: 1=carro, 7=moto)
 * @returns {Promise<Array<{Value: string|number, Label: string}>>}
 */
export async function listBrands({ type }) {
  // Converte tipo interno para código FIPE (1=carro, 2=moto, 3=caminhão)
  const fipeType = toFipeVehicleType(type);

  try {
    const codigoTabelaReferencia = await getReferenceTableCode();

    const resp = await fipeClient.post('/ConsultarMarcas', {
      codigoTabelaReferencia,
      codigoTipoVeiculo: fipeType,
    });

    return mapBrandsFromFipe(resp?.data);
  } catch (primaryError) {
    console.warn('[fipeService] listBrands — FIPE oficial falhou, tentando Parallelum...', primaryError?.message);

    try {
      const vehicleTypePath = getParallelumVehicleTypePath(fipeType);
      const resp = await parallelumClient.get(`/${vehicleTypePath}/brands`);
      return mapBrandsFromParallelum(resp?.data);
    } catch (fallbackError) {
      console.error('[fipeService] listBrands — Parallelum também falhou:', fallbackError?.message);
      throw fallbackError;
    }
  }
}

// ---------------------------------------------------------------------------
// Listar Modelos
// ---------------------------------------------------------------------------

/**
 * Busca a lista de modelos de uma marca.
 * Primário: API oficial FIPE → Fallback: Parallelum
 *
 * @param {{ brand: number|string, type: number }} params
 * @returns {Promise<Array<{Value: string|number, Label: string}>>}
 */
export async function listModels({ brand, type }) {
  const fipeType = toFipeVehicleType(type);

  try {
    const codigoTabelaReferencia = await getReferenceTableCode();

    const resp = await fipeClient.post('/ConsultarModelos', {
      codigoTabelaReferencia,
      codigoTipoVeiculo: fipeType,
      codigoMarca: brand,
    });

    return mapModelsFromFipe(resp?.data);
  } catch (primaryError) {
    console.warn('[fipeService] listModels — FIPE oficial falhou, tentando Parallelum...', primaryError?.message);

    try {
      const vehicleTypePath = getParallelumVehicleTypePath(fipeType);
      const resp = await parallelumClient.get(`/${vehicleTypePath}/brands/${brand}/models`);
      return mapModelsFromParallelum(resp?.data);
    } catch (fallbackError) {
      console.error('[fipeService] listModels — Parallelum também falhou:', fallbackError?.message);
      throw fallbackError;
    }
  }
}

// ---------------------------------------------------------------------------
// Listar Anos / Versões
// ---------------------------------------------------------------------------

/**
 * Busca a lista de anos-modelo de um modelo.
 * Primário: API oficial FIPE → Fallback: Parallelum
 *
 * @param {{ model: number|string, brand: number|string, type: number }} params
 * @returns {Promise<Array<{Value: string|number, Label: string}>>}
 */
export async function listYearModels({ model, brand, type }) {
  const fipeType = toFipeVehicleType(type);

  try {
    const codigoTabelaReferencia = await getReferenceTableCode();

    const resp = await fipeClient.post('/ConsultarAnoModelo', {
      codigoTabelaReferencia,
      codigoTipoVeiculo: fipeType,
      codigoMarca: brand,
      codigoModelo: model,
    });

    return mapYearsFromFipe(resp?.data);
  } catch (primaryError) {
    console.warn('[fipeService] listYearModels — FIPE oficial falhou, tentando Parallelum...', primaryError?.message);

    try {
      const vehicleTypePath = getParallelumVehicleTypePath(fipeType);
      const resp = await parallelumClient.get(`/${vehicleTypePath}/brands/${brand}/models/${model}/years`);
      return mapYearsFromParallelum(resp?.data);
    } catch (fallbackError) {
      console.error('[fipeService] listYearModels — Parallelum também falhou:', fallbackError?.message);
      throw fallbackError;
    }
  }
}

// ---------------------------------------------------------------------------
// Consultar Preço FIPE → enviar para API interna
// ---------------------------------------------------------------------------

/**
 * Busca o valor FIPE de um veículo (FIPE oficial → fallback Parallelum)
 * e envia os dados obtidos para a API interna (/getFipe).
 *
 * Payload enviado ao backend:
 * {
 *   fipeData: { Marca, Modelo, AnoModelo, Valor, Combustivel, CodigoFipe },
 *   brand: 21,
 *   model: 10289,
 *   type: 1,
 *   fuel: 1
 * }
 *
 * @param {{ fuel: number, yearModel: number, model: number|string, brand: number|string, type: number }} params
 * @returns {Promise<object>} — resposta da API interna
 */
export async function getFipe({ fuel, yearModel, model, brand, type }) {
  const fipeType = toFipeVehicleType(type);
  const year = yearModel?.split?.('-')?.[0] || yearModel;
  const code = yearModel?.split?.('-')?.[1];

  // 1) Buscar dados na FIPE oficial (ou Parallelum como fallback)
  let fipeData = null;

  try {
    const codigoTabelaReferencia = await getReferenceTableCode();
    const tipoVeiculoLabel = { 1: 'carro', 2: 'moto', 3: 'caminhao' };

    const resp = await fipeClient.post('/ConsultarValorComTodosParametros', {
      codigoTabelaReferencia,
      codigoMarca: brand,
      codigoModelo: model,
      codigoTipoVeiculo: fipeType,
      anoModelo: year,
      codigoTipoCombustivel: fuel,
      tipoVeiculo: tipoVeiculoLabel[fipeType] || 'carro',
      tipoConsulta: 'tradicional',
    });

    // Resposta FIPE oficial: { Marca, Modelo, AnoModelo, Valor, Combustivel, CodigoFipe, SiglaCombustivel, ... }
    fipeData = {
      Marca: resp?.data?.Marca,
      Modelo: resp?.data?.Modelo,
      AnoModelo: resp?.data?.AnoModelo,
      Valor: resp?.data?.Valor,
      Combustivel: resp?.data?.Combustivel,
      CodigoFipe: resp?.data?.CodigoFipe,
    };
  } catch (primaryError) {
    console.warn('[fipeService] getFipe — FIPE oficial falhou, tentando Parallelum...', primaryError?.message);

    try {
      const vehicleTypePath = getParallelumVehicleTypePath(fipeType);
      const resp = await parallelumClient.get(
        `/${vehicleTypePath}/brands/${brand}/models/${model}/years/${code ? year + '-' + code : year + '-' + fuel}`,
      );

      // Mapear resposta Parallelum para o mesmo formato de fipeData
      const data = resp?.data;
      fipeData = {
        Marca: data?.brand,
        Modelo: data?.model,
        AnoModelo: data?.modelYear,
        Valor: data?.price,
        Combustivel: data?.fuel,
        CodigoFipe: data?.codeFipe,
      };
    } catch (fallbackError) {
      console.error('[fipeService] getFipe — Parallelum também falhou:', fallbackError?.message);
      throw fallbackError;
    }
  }

  // 2) Enviar fipeData + códigos para a API interna
  const resp = await api({
    method: 'POST',
    url: '/getFipe',
    headers: {
      apiKey: 'a3607db1-530f-4565-93a1-7c2ff693c8db',
      apiSecret: 'f2b85508-8097-4adf-990a-6ba37b246fc1',
    },
    data: {
      fipeData,
      brand: Number(brand),
      model: Number(model),
      type: Number(type),
      fuel: Number(fuel),
    },
  });

  return resp?.data;
}

